import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Calendar, MapPin, Sparkles, TrendingUp,
  BookOpen, GraduationCap, School, Globe2, Trophy, Cpu, ChevronRight,
  Clock, Bookmark, PenLine
} from "lucide-react";

/* ---------- Palette (warm premium editorial) ---------- */
const C = {
  bg: "#FBF7EF",
  ink: "#1B1712",
  text2: "#5C564D",
  terracotta: "#B45525",
  olive: "#4A5D4E",
  border: "#E5DFD3",
  surface: "#F2EBE1",
};

/* ---------- Sample data ---------- */
const TICKER = [
  "CBSE announces Class 12 exam reform 2026",
  "UGC opens draft regulation for foreign university campuses",
  "IIT Bombay sets a new placement record: ₹4.28 Cr top offer",
  "AIIMS Delhi launches AI radiology fellowship",
  "NEET-UG 2026 syllabus rationalisation released",
];

const HERO = {
  overline: "Cover Story · Higher Education",
  title: "Inside India's silent revolution in higher education",
  dek: "A generation of state universities is quietly outpacing the IITs on employability. Nine months of reporting across four states reveals why the next great Indian institution may not be in a metro at all.",
  author: "By Ananya Iyer",
  date: "27 July 2026",
  read: "12 min read",
  image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1400&q=80",
};

const SECONDARY = [
  {
    overline: "Policy",
    title: "The UGC's foreign university draft — winners, losers, and the fine print",
    author: "Ravi Menon",
    read: "8 min",
    image: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=900&q=80",
  },
  {
    overline: "Rankings",
    title: "NIRF 2026: How Bengaluru overtook Delhi as India's academic capital",
    author: "Priya Nair",
    read: "6 min",
    image: "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=900&q=80",
  },
  {
    overline: "EdTech",
    title: "After the crash: which of India's ten unicorn edtechs survived 2025?",
    author: "Kabir Shah",
    read: "9 min",
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=900&q=80",
  },
];

const CATEGORIES = [
  { key: "university", label: "University & College", count: 1284, icon: GraduationCap, tint: "#B45525" },
  { key: "school", label: "School News", count: 962, icon: School, tint: "#4A5D4E" },
  { key: "edtech", label: "EdTech", count: 674, icon: Cpu, tint: "#8A5A2B" },
  { key: "foreign", label: "Foreign Education", count: 431, icon: Globe2, tint: "#6D6247" },
  { key: "ai", label: "AI in Education", count: 258, icon: Sparkles, tint: "#B45525" },
  { key: "rankings", label: "Rankings", count: 74, icon: Trophy, tint: "#4A5D4E" },
];

const LATEST = [
  { cat: "University", title: "Delhi University restores four-year honours structure after 12 years", dek: "The academic council voted 68-14 after a month of consultations with department heads.", author: "Neha Kapoor", time: "2h ago", image: "https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?auto=format&fit=crop&w=600&q=80" },
  { cat: "School", title: "Kerala rolls out mother-tongue-first primary curriculum in 4,200 schools", dek: "Backed by a landmark linguistics study from IISc, the reform targets literacy retention.", author: "Meera Thomas", time: "4h ago", image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=600&q=80" },
  { cat: "EdTech", title: "Byju's remains under NCLT watch as fresh promoter bid falls through", dek: "Creditors have 21 days to respond to the revised resolution plan.", author: "Kabir Shah", time: "6h ago", image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=600&q=80" },
  { cat: "Study Abroad", title: "US student-visa approvals for Indians fall 34% — what changed?", dek: "Consular data reveals a policy shift the State Department has yet to formally announce.", author: "Arjun Verma", time: "8h ago", image: "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&w=600&q=80" },
  { cat: "AI", title: "IIT Madras releases first Indic-language education LLM under Apache 2.0", dek: "Trained on 40B tokens of school and undergraduate content across 8 Indian languages.", author: "Ananya Iyer", time: "yesterday", image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=600&q=80" },
];

const RANKINGS = [
  {
    title: "NIRF Top Engineering",
    year: "2026",
    items: ["IIT Madras", "IIT Delhi", "IIT Bombay", "IIT Kanpur", "IIT Kharagpur"],
  },
  {
    title: "NIRF Top Universities",
    year: "2026",
    items: ["IISc Bengaluru", "JNU Delhi", "Jamia Millia Islamia", "Manipal Academy", "University of Hyderabad"],
  },
  {
    title: "Top Schools in India",
    year: "2026",
    items: ["DPS R.K. Puram, Delhi", "The Doon School, Dehradun", "Modern School, Delhi", "Bishop Cotton, Bengaluru", "La Martiniere, Kolkata"],
  },
];

const EVENTS = [
  { d: "12", m: "Aug", title: "JEE Advanced 2026 — Counselling Round 3", where: "Nationwide, Online" },
  { d: "18", m: "Aug", title: "IIT Bombay Techfest — Curtain Raiser", where: "Powai, Mumbai" },
  { d: "02", m: "Sep", title: "AIIMS PG Interview Window Opens", where: "New Delhi" },
  { d: "14", m: "Sep", title: "EduTech India Summit 2026", where: "Bengaluru International Centre" },
  { d: "28", m: "Sep", title: "NEET-UG Second Attempt", where: "Nationwide" },
  { d: "05", m: "Oct", title: "Foreign Universities Expo — East India", where: "Kolkata" },
];

const CITIES = ["New Delhi", "Bengaluru", "Mumbai", "Pune", "Hyderabad", "Chennai", "Kolkata", "Ahmedabad", "Chandigarh", "Kochi", "Jaipur", "Lucknow"];

const EDITORS_PICKS = [
  { title: "The quiet ascent of India's regional engineering colleges", note: "Why our data desk spent nine months in Andhra Pradesh.", author: "The Editors", image: "https://images.unsplash.com/photo-1591123120675-6f7f1aae0e5b?auto=format&fit=crop&w=900&q=80" },
  { title: "What the National Curriculum Framework really says about screens", note: "A close reading of the 634-page document — page by page.", author: "Priya Nair", image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=900&q=80" },
  { title: "India's teacher shortage is a math problem — literally", note: "One state's radical fix could reshape the profession.", author: "Ravi Menon", image: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?auto=format&fit=crop&w=900&q=80" },
];

const TRENDING = [
  "The New IIT: inside the campus that rejected coaching-culture applicants",
  "AIIMS releases first National Health Curriculum draft",
  "How Kota bounced back — and what its 2026 batch looks like",
  "The MBBS seat surplus nobody is talking about",
  "Duolingo India: 41% growth, and a quiet pivot",
];

/* ---------- Reusable atoms ---------- */
const Overline = ({ children, color = C.terracotta, testId }) => (
  <div data-testid={testId} className="uppercase" style={{ color, fontSize: 11, letterSpacing: "0.22em", fontWeight: 700 }}>{children}</div>
);

const Rule = ({ vertical = false, style = {} }) => (
  <div style={{
    background: C.border,
    ...(vertical ? { width: 1, alignSelf: "stretch" } : { height: 1, width: "100%" }),
    ...style
  }} />
);

/* ---------- Page ---------- */
export default function HomepageSamplePage() {
  const [scrolled, setScrolled] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(Math.min(1, window.scrollY / 400));
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="sample-homepage" style={{ background: C.bg, color: C.ink, fontFamily: "Outfit, system-ui, sans-serif" }}>
      <style>{`
        @keyframes tickerRun { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .ticker-track { animation: tickerRun 45s linear infinite; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .fadeup { animation: fadeUp 0.7s ease-out both; }
        .story-lift { transition: transform 260ms ease, color 260ms ease; }
        .story-lift:hover { transform: translateY(-2px); }
        .story-lift:hover .story-title { color: ${C.terracotta}; }
        .cat-card { transition: background 260ms ease, transform 260ms ease; }
        .cat-card:hover { background: ${C.surface}; transform: translateY(-2px); }
        .pill { transition: all 220ms ease; }
        .pill:hover { background: ${C.ink}; color: ${C.bg}; border-color: ${C.ink}; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { scrollbar-width: none; }
        .sample-serif { font-family: 'Playfair Display', 'Fraunces', Georgia, serif; letter-spacing: -0.015em; }
        .drop-cap::first-letter { font-family: 'Playfair Display', serif; font-weight: 800; font-size: 3.2em; line-height: 0.82; float: left; padding: 0.35rem 0.6rem 0 0; color: ${C.terracotta}; }
        .grain { position: relative; }
        .grain::before {
          content: ""; position: absolute; inset: 0; pointer-events: none;
          background-image: radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px);
          background-size: 3px 3px; opacity: 0.35; mix-blend-mode: multiply;
        }
      `}</style>

      {/* ================= TICKER ================= */}
      <div style={{ background: C.ink, color: C.bg }} className="overflow-hidden" data-testid="sample-ticker">
        <div className="flex whitespace-nowrap ticker-track py-2">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="mx-8 text-[11px] uppercase tracking-[0.28em] font-semibold flex items-center gap-3">
              <span style={{ width: 6, height: 6, background: C.terracotta, borderRadius: 999 }}/>
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* ================= MASTHEAD ================= */}
      <header className="border-b" style={{ borderColor: C.border, background: `rgba(251,247,239,${0.85 + scrolled*0.15})`, backdropFilter: "blur(14px)", position: "sticky", top: 0, zIndex: 40 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 text-center">
          <div className="uppercase" style={{ color: C.terracotta, fontSize: 10, letterSpacing: "0.4em", fontWeight: 700 }}>Vol. XI · Established 2024</div>
          <h1 className="sample-serif mt-2" style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", lineHeight: 1, fontWeight: 800 }}>EduTime Today</h1>
          <div className="mt-2 flex items-center justify-center gap-3" style={{ color: C.text2, fontSize: 12, letterSpacing: "0.15em" }}>
            <span className="italic sample-serif">India's paper of record for education</span>
            <span style={{ width: 4, height: 4, background: C.terracotta, borderRadius: 999 }}/>
            <span className="uppercase">Monday, 27 July 2026</span>
          </div>
        </div>
        <div className="border-t" style={{ borderColor: C.border }}>
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-6 overflow-x-auto hide-scrollbar" data-testid="sample-nav">
            <div className="flex items-center gap-6 text-[13px] uppercase whitespace-nowrap" style={{ letterSpacing: "0.16em", fontWeight: 600 }}>
              {["Home", "Latest", "Universities", "Schools", "EdTech", "Foreign", "AI", "Rankings", "Events", "Jobs", "Directory"].map(n => (
                <a key={n} href="#" data-testid={`sample-nav-${n.toLowerCase()}`} className="hover:opacity-70" style={{ color: n === "Home" ? C.terracotta : C.ink }}>{n}</a>
              ))}
            </div>
            <button className="text-[11px] uppercase tracking-[0.22em] font-bold px-4 py-2 border" style={{ borderColor: C.ink, color: C.ink }} data-testid="sample-subscribe-cta">Subscribe</button>
          </nav>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 fadeup" data-testid="sample-hero">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <Overline testId="sample-hero-overline">{HERO.overline}</Overline>
            <h2 className="sample-serif mt-4" style={{ fontSize: "clamp(2.4rem, 5.2vw, 4.6rem)", lineHeight: 1.02, fontWeight: 800, color: C.ink }} data-testid="sample-hero-title">
              {HERO.title}
            </h2>
            <p className="mt-6 max-w-2xl drop-cap" style={{ color: C.text2, fontSize: 18, lineHeight: 1.7 }} data-testid="sample-hero-dek">
              {HERO.dek}
            </p>
            <div className="mt-8 flex items-center gap-4 text-sm" style={{ color: C.text2 }}>
              <span className="sample-serif italic" style={{ color: C.ink }}>{HERO.author}</span>
              <Rule style={{ width: 24 }}/>
              <span className="uppercase tracking-[0.18em] text-[11px]">{HERO.date}</span>
              <span>·</span>
              <span className="flex items-center gap-1 text-[12px]"><Clock className="w-3 h-3"/>{HERO.read}</span>
            </div>
            <div className="mt-8 flex items-center gap-3">
              <a href="#" className="inline-flex items-center gap-2 px-5 py-3 text-[12px] uppercase tracking-[0.22em] font-bold" style={{ background: C.ink, color: C.bg }} data-testid="sample-hero-read-btn">
                Read the story <ArrowUpRight className="w-4 h-4"/>
              </a>
              <a href="#" className="inline-flex items-center gap-2 px-5 py-3 text-[12px] uppercase tracking-[0.22em] font-bold border" style={{ borderColor: C.ink, color: C.ink }} data-testid="sample-hero-save-btn">
                <Bookmark className="w-4 h-4"/> Save
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative grain overflow-hidden" style={{ background: C.surface }} data-testid="sample-hero-image">
              <img src={HERO.image} alt="" className="w-full aspect-[4/5] object-cover" />
            </div>
            <div className="mt-3 text-[11px] uppercase tracking-[0.2em]" style={{ color: C.text2 }}>Photograph · EduTime Photo Desk</div>
          </div>
        </div>
      </section>

      <Rule/>

      {/* ================= SECONDARY STORIES ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="sample-secondary">
        <div className="grid md:grid-cols-3 gap-0">
          {SECONDARY.map((s, idx) => (
            <a key={idx} href="#" data-testid={`sample-secondary-${idx}`} className="story-lift group px-6 first:pl-0 last:pr-0 border-l md:[&:first-child]:border-l-0" style={{ borderColor: C.border }}>
              <div className="overflow-hidden mb-4" style={{ background: C.surface }}>
                <img src={s.image} alt="" className="w-full aspect-[3/2] object-cover"/>
              </div>
              <Overline color={C.olive}>{s.overline}</Overline>
              <h3 className="sample-serif mt-3 story-title" style={{ fontSize: 22, lineHeight: 1.15, fontWeight: 700, color: C.ink }}>{s.title}</h3>
              <div className="mt-3 text-[12px] flex items-center gap-3" style={{ color: C.text2 }}>
                <span className="sample-serif italic" style={{ color: C.ink }}>{s.author}</span>
                <span>·</span>
                <span className="uppercase tracking-[0.18em]">{s.read}</span>
              </div>
            </a>
          ))}
        </div>
      </section>

      <Rule/>

      {/* ================= DISCOVERY: EXPLORE BY CATEGORY ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sample-categories">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <Overline color={C.terracotta}>Discover</Overline>
            <h3 className="sample-serif mt-3" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 800, lineHeight: 1 }}>Explore by section</h3>
            <p className="mt-3 max-w-xl" style={{ color: C.text2, fontSize: 16, lineHeight: 1.6 }}>Six desks, one publication. Follow the beat that matters to you.</p>
          </div>
          <a href="#" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold" style={{ color: C.ink }} data-testid="sample-categories-all">All sections <ArrowRight className="w-4 h-4"/></a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-t border-l" style={{ borderColor: C.border }}>
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            return (
              <a key={cat.key} href="#" data-testid={`sample-category-${cat.key}`} className="cat-card p-8 border-r border-b flex flex-col justify-between min-h-[190px]" style={{ borderColor: C.border }}>
                <div className="flex items-start justify-between">
                  <div style={{ width: 44, height: 44, background: cat.tint, color: C.bg }} className="grid place-items-center">
                    <Icon className="w-5 h-5"/>
                  </div>
                  <ArrowUpRight className="w-4 h-4" style={{ color: C.text2 }}/>
                </div>
                <div>
                  <div className="sample-serif" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15 }}>{cat.label}</div>
                  <div className="mt-2 text-[12px] uppercase tracking-[0.2em]" style={{ color: C.text2 }}>{cat.count.toLocaleString()} stories</div>
                </div>
              </a>
            );
          })}
        </div>
      </section>

      <Rule/>

      {/* ================= LATEST RIVER + TRENDING RAIL ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sample-latest">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <Overline color={C.olive}>The Desk</Overline>
                <h3 className="sample-serif mt-3" style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>Latest, in depth</h3>
              </div>
              <a href="#" className="text-[12px] uppercase tracking-[0.22em] font-bold" data-testid="sample-latest-all">Full archive →</a>
            </div>
            <div className="divide-y" style={{ borderColor: C.border }}>
              {LATEST.map((n, idx) => (
                <a key={idx} href="#" data-testid={`sample-latest-${idx}`} className="story-lift block py-6 first:pt-0">
                  <div className="grid grid-cols-[1fr_120px] sm:grid-cols-[1fr_180px] gap-6 items-start">
                    <div>
                      <Overline color={C.terracotta}>{n.cat}</Overline>
                      <h4 className="sample-serif mt-2 story-title" style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 700 }}>{n.title}</h4>
                      <p className="mt-2 text-[15px]" style={{ color: C.text2, lineHeight: 1.6 }}>{n.dek}</p>
                      <div className="mt-3 text-[12px] flex items-center gap-3" style={{ color: C.text2 }}>
                        <span className="sample-serif italic" style={{ color: C.ink }}>{n.author}</span>
                        <span>·</span>
                        <span className="uppercase tracking-[0.16em]">{n.time}</span>
                      </div>
                    </div>
                    <div className="overflow-hidden" style={{ background: C.surface }}>
                      <img src={n.image} alt="" className="w-full aspect-[4/3] object-cover"/>
                    </div>
                  </div>
                  <div className="mt-6" style={{ height: 1, background: C.border }}/>
                </a>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-40 space-y-10">
              {/* Trending */}
              <div data-testid="sample-trending">
                <Overline color={C.terracotta}>Most Read this week</Overline>
                <h3 className="sample-serif mt-3 mb-5" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>Trending on EduTime</h3>
                <ol className="space-y-5">
                  {TRENDING.map((t, i) => (
                    <li key={i} className="grid grid-cols-[38px_1fr] gap-4 items-start" data-testid={`sample-trending-${i}`}>
                      <div className="sample-serif" style={{ fontSize: 32, fontWeight: 800, color: C.terracotta, lineHeight: 1 }}>{String(i+1).padStart(2, "0")}</div>
                      <a href="#" className="sample-serif story-title story-lift block" style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: C.ink }}>{t}</a>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Editor's note */}
              <div className="border p-6" style={{ borderColor: C.border, background: C.surface }} data-testid="sample-editors-note">
                <div className="flex items-center gap-3">
                  <div className="grid place-items-center" style={{ width: 40, height: 40, background: C.ink, color: C.bg }}><PenLine className="w-4 h-4"/></div>
                  <div>
                    <Overline color={C.olive}>From the Editor</Overline>
                    <div className="sample-serif italic mt-1" style={{ fontSize: 14 }}>Ananya Iyer, Editor-in-Chief</div>
                  </div>
                </div>
                <p className="sample-serif mt-4" style={{ fontSize: 18, lineHeight: 1.55, color: C.ink }}>
                  “The most important education story of this decade will not come from Delhi. Our reporters are already there.”
                </p>
                <a href="#" className="mt-4 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold" style={{ color: C.terracotta }} data-testid="sample-editors-note-cta">Read the letter <ArrowRight className="w-4 h-4"/></a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Rule/>

      {/* ================= RANKINGS ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sample-rankings">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <Overline color={C.terracotta}>Discover</Overline>
            <h3 className="sample-serif mt-3" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 800, lineHeight: 1 }}>Rankings, at a glance</h3>
            <p className="mt-3 max-w-xl" style={{ color: C.text2, fontSize: 16, lineHeight: 1.6 }}>Curated, audited, updated weekly. From engineering to schools — the leaderboards that matter.</p>
          </div>
          <a href="#" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold" data-testid="sample-rankings-all">All rankings <ArrowRight className="w-4 h-4"/></a>
        </div>
        <div className="grid md:grid-cols-3 gap-0 border-t border-l" style={{ borderColor: C.border }}>
          {RANKINGS.map((r, i) => (
            <div key={i} className="p-8 border-r border-b" style={{ borderColor: C.border }} data-testid={`sample-ranking-${i}`}>
              <Overline color={C.olive}>{r.year} · Verified</Overline>
              <h4 className="sample-serif mt-3" style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15 }}>{r.title}</h4>
              <ol className="mt-6 space-y-3">
                {r.items.map((it, j) => (
                  <li key={j} className="grid grid-cols-[38px_1fr_auto] items-center gap-3 pb-3 border-b" style={{ borderColor: C.border }}>
                    <span className="sample-serif" style={{ fontSize: 22, color: C.terracotta, fontWeight: 800, lineHeight: 1 }}>{String(j+1).padStart(2, "0")}</span>
                    <span className="sample-serif" style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{it}</span>
                    <ChevronRight className="w-4 h-4" style={{ color: C.text2 }}/>
                  </li>
                ))}
              </ol>
              <a href="#" className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: C.terracotta }} data-testid={`sample-ranking-${i}-cta`}>View full ranking <ArrowUpRight className="w-3.5 h-3.5"/></a>
            </div>
          ))}
        </div>
      </section>

      <Rule/>

      {/* ================= EVENTS SCROLLER ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sample-events">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <Overline color={C.olive}>Discover</Overline>
            <h3 className="sample-serif mt-3" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 800, lineHeight: 1 }}>The calendar</h3>
            <p className="mt-3 max-w-xl" style={{ color: C.text2, fontSize: 16, lineHeight: 1.6 }}>Six weeks of admissions, exams and summits worth clearing your desk for.</p>
          </div>
          <a href="#" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold" data-testid="sample-events-all">Full calendar <ArrowRight className="w-4 h-4"/></a>
        </div>
        <div className="flex gap-6 overflow-x-auto hide-scrollbar pb-2 snap-x snap-mandatory">
          {EVENTS.map((e, idx) => (
            <div key={idx} className="snap-start min-w-[280px] md:min-w-[320px] border p-6 flex flex-col justify-between story-lift" style={{ borderColor: C.border, background: idx % 2 === 0 ? C.surface : "transparent" }} data-testid={`sample-event-${idx}`}>
              <div>
                <div className="flex items-baseline gap-3">
                  <div className="sample-serif" style={{ fontSize: 56, fontWeight: 800, lineHeight: 0.9, color: C.terracotta }}>{e.d}</div>
                  <div className="uppercase tracking-[0.24em] text-[11px] font-bold" style={{ color: C.ink }}>{e.m} 2026</div>
                </div>
                <h4 className="sample-serif mt-6 story-title" style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>{e.title}</h4>
                <div className="mt-3 flex items-center gap-2 text-[12px]" style={{ color: C.text2 }}>
                  <MapPin className="w-3.5 h-3.5"/>{e.where}
                </div>
              </div>
              <a href="#" className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: C.terracotta }} data-testid={`sample-event-${idx}-cta`}>Add to calendar <ArrowUpRight className="w-3.5 h-3.5"/></a>
            </div>
          ))}
        </div>
      </section>

      <Rule/>

      {/* ================= CITY PILLS ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sample-cities">
        <div className="grid md:grid-cols-[1fr_2fr] gap-10 items-start">
          <div>
            <Overline color={C.terracotta}>Discover</Overline>
            <h3 className="sample-serif mt-3" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 800, lineHeight: 1 }}>By city</h3>
            <p className="mt-3" style={{ color: C.text2, fontSize: 16, lineHeight: 1.6 }}>The stories, campuses and boards shaping education where you live.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {CITIES.map(city => (
              <a key={city} href="#" data-testid={`sample-city-${city.toLowerCase().replace(/\s+/g,'-')}`} className="pill inline-flex items-center gap-2 border px-5 py-2.5 text-[13px]" style={{ borderColor: C.border, color: C.ink }}>
                <MapPin className="w-3.5 h-3.5"/>{city}
              </a>
            ))}
          </div>
        </div>
      </section>

      <Rule/>

      {/* ================= EDITOR'S PICKS ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sample-editors-picks">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <Overline color={C.olive}>Curated</Overline>
            <h3 className="sample-serif mt-3" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)", fontWeight: 800, lineHeight: 1 }}>Editor's picks</h3>
            <p className="mt-3 max-w-xl" style={{ color: C.text2, fontSize: 16, lineHeight: 1.6 }}>Three long-form reads our editors couldn't put down this week.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-0 border-t border-l" style={{ borderColor: C.border }}>
          {EDITORS_PICKS.map((p, i) => (
            <a key={i} href="#" data-testid={`sample-editors-pick-${i}`} className="story-lift block border-r border-b p-8" style={{ borderColor: C.border }}>
              <div className="overflow-hidden mb-6" style={{ background: C.surface }}>
                <img src={p.image} alt="" className="w-full aspect-[4/3] object-cover"/>
              </div>
              <Overline color={C.terracotta}>Editor's Pick · {String(i+1).padStart(2,"0")}</Overline>
              <h4 className="sample-serif mt-3 story-title" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>{p.title}</h4>
              <p className="sample-serif italic mt-3" style={{ fontSize: 14, color: C.text2 }}>“{p.note}”</p>
              <div className="mt-4 text-[12px] uppercase tracking-[0.2em]" style={{ color: C.text2 }}>{p.author}</div>
            </a>
          ))}
        </div>
      </section>

      {/* ================= NEWSLETTER ================= */}
      <section style={{ background: C.ink, color: C.bg }} data-testid="sample-newsletter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Overline color={C.terracotta}>The EduTime Weekly</Overline>
            <h3 className="sample-serif mt-3" style={{ fontSize: "clamp(2rem, 3.8vw, 3.2rem)", fontWeight: 800, lineHeight: 1.05 }}>
              The one email India's education leaders read on Sunday.
            </h3>
            <p className="mt-5" style={{ color: "#D8D0C1", fontSize: 16, lineHeight: 1.7, maxWidth: 520 }}>
              A hand-edited digest of the week's most important stories, five under-the-radar reads and one interview. No AI slop. No sponsored links.
            </p>
            <div className="mt-4 flex items-center gap-4 text-[12px] uppercase tracking-[0.2em]" style={{ color: "#D8D0C1" }}>
              <span><b style={{ color: C.bg }}>42,000+</b> subscribers</span>
              <span>·</span>
              <span>Sundays, 07:00 IST</span>
            </div>
          </div>
          <form className="border p-2 flex flex-col sm:flex-row gap-2" style={{ borderColor: "#3A322A", background: "#241E18" }} onSubmit={(e)=>e.preventDefault()} data-testid="sample-newsletter-form">
            <input
              type="email"
              placeholder="you@university.ac.in"
              className="flex-1 bg-transparent px-4 py-3 outline-none text-[15px]"
              style={{ color: C.bg }}
              data-testid="sample-newsletter-email"
            />
            <button type="submit" className="px-6 py-3 text-[12px] uppercase tracking-[0.22em] font-bold inline-flex items-center justify-center gap-2" style={{ background: C.terracotta, color: C.bg }} data-testid="sample-newsletter-submit">
              Subscribe <ArrowRight className="w-4 h-4"/>
            </button>
          </form>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t" style={{ borderColor: C.border }} data-testid="sample-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-4 gap-10">
          <div>
            <div className="sample-serif" style={{ fontSize: 28, fontWeight: 800 }}>EduTime Today</div>
            <p className="mt-3 text-[13px]" style={{ color: C.text2, lineHeight: 1.6 }}>Independent, journalist-led coverage of Indian education. AI-assisted, human-edited, always sourced.</p>
          </div>
          <div>
            <Overline color={C.olive}>Sections</Overline>
            <ul className="mt-4 space-y-2 text-[14px]">
              {["Universities", "Schools", "EdTech", "Foreign Education", "Rankings", "Events"].map(x => <li key={x}><a href="#" className="hover:underline">{x}</a></li>)}
            </ul>
          </div>
          <div>
            <Overline color={C.olive}>The paper</Overline>
            <ul className="mt-4 space-y-2 text-[14px]">
              {["About", "Editorial standards", "Corrections", "Contact the desk", "Advertise", "Careers"].map(x => <li key={x}><a href="#" className="hover:underline">{x}</a></li>)}
            </ul>
          </div>
          <div>
            <Overline color={C.olive}>Follow</Overline>
            <ul className="mt-4 space-y-2 text-[14px]">
              {["Newsletter", "RSS", "LinkedIn", "X / Twitter", "YouTube"].map(x => <li key={x}><a href="#" className="hover:underline">{x}</a></li>)}
            </ul>
          </div>
        </div>
        <div className="border-t" style={{ borderColor: C.border }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between text-[11px] uppercase tracking-[0.22em]" style={{ color: C.text2 }}>
            <span>© 2026 EduTime Today · An Indian publication</span>
            <span className="flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5"/> AI-assisted journalism · human-edited</span>
          </div>
        </div>
      </footer>

      {/* ================= FLOATING SAMPLE RIBBON ================= */}
      <div
        data-testid="sample-preview-ribbon"
        className="fixed bottom-5 right-5 z-50 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold shadow-lg flex items-center gap-2"
        style={{ background: C.terracotta, color: C.bg }}
      >
        <BookOpen className="w-3.5 h-3.5"/> Sample preview · not live yet
      </div>
    </div>
  );
}
